// Layout component for dashboard pages
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthContext } from '@/contexts/auth-context'
import { 
  LayoutDashboard, 
  CheckSquare, 
  Users, 
  TableProperties, 
  LineChart,
  LogOut,
  Menu,
  UserCircle,
  DollarSign,
  Gift,
  TrendingUp,
  PieChart,
  Clock,
  Calculator,
  Shield,
  Zap,
  Target,
  Settings,
  Sparkles
} from 'lucide-react'
import { useState } from 'react'
import { useUser } from '@/lib/hooks'
import { useOrganization } from '@/lib/hooks/useOrganization'
import ProjectSelector from '@/components/project-selector'
import OrganizationSelector from '@/components/organization-selector'
import RoleIndicator from '@/components/custom-ui/role-indicator'

const navigationSections = [
  {
    title: 'Work Commitments',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Tasks', href: '/tasks', icon: CheckSquare },
      { name: 'Contributions', href: '/contributions', icon: Users },
      { name: 'Team', href: '/users', icon: Users },
    ]
  },
  {
    title: 'Cap Table',
    items: [
      { name: 'Cap Table', href: '/cap-table', icon: TableProperties },
      { name: 'Share Authorizations', href: '/share-authorizations', icon: Shield },
      { name: 'Equity Calculator', href: '/equity-recommendations', icon: Calculator },
      { name: 'Option Tracking', href: '/option-tracking', icon: Clock },
      { name: 'Option Exercises', href: '/option-exercises', icon: Zap },
      { name: 'Vesting', href: '/vesting', icon: Clock },
      { name: 'Milestones', href: '/milestones', icon: Target },
    ]
  },
  {
    title: 'Financial',
    items: [
      { name: 'Valuations', href: '/valuations', icon: TrendingUp },
      { name: 'Funding', href: '/funding-rounds', icon: DollarSign },
      { name: 'Equity Offers', href: '/equity-offers', icon: Gift },
      { name: 'Exit Scenarios', href: '/exit-scenarios', icon: PieChart },
      { name: 'Simulations', href: '/simulations', icon: LineChart },
    ]
  },
  {
    title: 'Settings',
    items: [
      { name: 'API & Integrations', href: '/api-settings', icon: Settings },
    ]
  }
]

export default function DashboardLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { profile } = useUser()
  const { currentOrganization } = useOrganization()
  const { signOut } = useAuthContext()

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar toggle */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-gray-500 hover:text-gray-600"
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          <div className="flex flex-col px-4 py-3 border-b border-gray-200">
            <h1 className="text-xl font-semibold mb-3">Share Lattice</h1>
            <div className="space-y-2">
              <OrganizationSelector />
              {currentOrganization && <ProjectSelector />}
            </div>
          </div>
          
          <nav className="flex-1 px-4 py-4 space-y-6 overflow-y-auto">
            {navigationSections.map((section) => (
              <div key={section.title} className="space-y-1">
                <h3 className="px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {section.title}
                </h3>
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.href
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          isActive
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <item.icon className="mr-3 h-5 w-5" />
                        {item.name}
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-200 space-y-2">
            <RoleIndicator />
            <Link
              to="/profile"
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <UserCircle className="mr-3 h-5 w-5" />
              <div className="text-left">
                <div>{profile?.first_name} {profile?.last_name}</div>
                <div className="text-xs text-gray-500">View Profile</div>
              </div>
            </Link>
            <Link
              to="/onboarding"
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <Sparkles className="mr-3 h-5 w-5" />
              Setup Wizard
            </Link>
            <button
              onClick={handleSignOut}
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        <main className="py-6 px-4 sm:px-6 lg:px-8 pt-16 lg:pt-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}